import React, { useContext, useMemo } from "react";
import { useRegUIClient } from "@foundation/reg-ui-client";
import styles from "./styles.module.css";
import { usePortfolioPage } from "~context/PortfolioPageContext";
import { ExportButton } from "~components/ExportButton";
import { ExportOptions, ImportedData } from "~utils/fileTransfer";
import { ImportButton } from "~components/ImportButton";
import { PageContext } from "~context/PageContext";
import { PortfolioPageData } from "~types/portfolio";
import {
  getPortfolioFormData,
  getWatchlistSheet,
} from "~components/WatchlistFileTransfer/fileTransferUtils";

type WatchlistFileTransferProps = {
  type: "export" | "import";
  onBeforeImport?: () => void;
};

export default function WatchlistFileTransfer(
  props: WatchlistFileTransferProps
) {
  const { type, onBeforeImport } = props;
  const { updatePortfolioForm } = usePortfolioPage();
  const { selectedPortInfo, selectedPortfolio } = usePortfolioPage();

  const { session } = useRegUIClient();
  const isBloombergEmployee = session?.userInfo?.user?.isBloombergEmployee;

  const pageContext = useContext(PageContext);

  // enable feature only for internal users and when flag is enabled
  const importExportFeatureEnabled = Boolean(
    isBloombergEmployee &&
      (pageContext?.pageData as PortfolioPageData)?.enableWatchlistImportExport
  );

  const watchlistSheetOptions: ExportOptions = useMemo(() => {
    if (
      !selectedPortInfo?.id ||
      !selectedPortInfo?.name ||
      !selectedPortfolio?.positions?.length
    ) {
      return {
        sheets: [],
      };
    }

    return getWatchlistSheet(selectedPortfolio);
  }, [selectedPortInfo?.id, selectedPortInfo?.name, selectedPortfolio]);

  const handleImportSheet = (data: ImportedData) => {
    const portfolioFormData = getPortfolioFormData(data);

    // open portfolio modal before setting imported data
    // so the default model behavior doesn't override data we are setting.
    onBeforeImport?.();

    setTimeout(() => {
      // update portfolio form to render in portfolio modal
      updatePortfolioForm(portfolioFormData);
    }, 0);
  };

  // Enable import/export feature based on feature flag
  if (!importExportFeatureEnabled) {
    return null;
  }

  return (
    <div className={styles.fileTransfer}>
      {type === "export" && (
        <ExportButton
          className={styles.button}
          options={watchlistSheetOptions}
          type="xlsx"
          outputName={selectedPortInfo?.name}
        >
          Export
        </ExportButton>
      )}

      {type === "import" && (
        <ImportButton onImport={handleImportSheet} className={styles.button}>
          Import
        </ImportButton>
      )}
    </div>
  );
}
